import { useContext, useEffect, useState } from "react"
import { toast } from "sonner"
import i18n from "@/lib/i18n/i18n"
import { useFeatureFlagEnabled } from "posthog-js/react"
import useRouteAlertConfig from "@/hooks/useRouteAlertConfig"
import { XmppContext } from "@/stream/xmppClient"

export type TemperatureRanges = {
  t1Min: string
  t1Max: string
  t2Min: string
  t2Max: string
}

const EMPTY_RANGES: TemperatureRanges = {
  t1Min: "",
  t1Max: "",
  t2Min: "",
  t2Max: "",
}

export const useTemperatureAlertSettings = (driverJid: string) => {
  const { client } = useContext(XmppContext)
  const { routeAlertConfig, updateAlertConfig, isLoading } =
    useRouteAlertConfig(client, driverJid)
  const enabled = Boolean(useFeatureFlagEnabled("temperature_alerts"))
  const [ranges, setRanges] = useState<TemperatureRanges>(EMPTY_RANGES)

  useEffect(() => {
    if (routeAlertConfig) {
      setRanges({
        t1Min: routeAlertConfig.temperature_main_alert_level_min || "",
        t1Max: routeAlertConfig.temperature_main_alert_level_max || "",
        t2Min: routeAlertConfig.temperature_additional_alert_level_min || "",
        t2Max: routeAlertConfig.temperature_additional_alert_level_max || "",
      })
    }
  }, [routeAlertConfig])

  const change = (field: keyof TemperatureRanges, value: string) =>
    setRanges((previous) => ({ ...previous, [field]: value }))

  const save = async () => {
    try {
      await updateAlertConfig({
        temperature_main_alert_level_min: ranges.t1Min || undefined,
        temperature_main_alert_level_max: ranges.t1Max || undefined,
        temperature_additional_alert_level_min: ranges.t2Min || undefined,
        temperature_additional_alert_level_max: ranges.t2Max || undefined,
      })
    } catch (error) {
      toast.error(i18n.t("app.temperature.saveFailed"))
      console.error(error)
    }
  }

  return { enabled, isLoading, ranges, change, save }
}
